import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { company } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  ProductBadges,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { calcDiscountPercentage } from '~/utils/formaters';

import PriceSection from '../../shared/PriceSection/PriceSection';

import styles from './ProductCardXL.module.scss';

/**
 * ProductCardXL - Former large card size (338px web)
 */
const ProductCardXL = observer(
  ({
    product,
    className,
    showColorVariants = false,
    productState,
    disabled = false,
  }: ProductCardVariantProps) => {
    const { handleClickProduct } = useCategoryAnalytics();

    const {
      currentItem,
      image,
      oldPrice,
      currentPrice,
      totalCartPrice,
      cartItemsCount,
      isFavorite,
      isOutOfStock,
      colorVariants,
      badges,
      multiBuyInfo,
      selectOffer,
      toggleFavorite,
    } = useProductCardV3(
      productState?.data ?? product,
      productState?.selectedOffer,
      'xl',
    );

    const handleProductClick = useCallback(() => {
      handleClickProduct(product);
    }, [product, handleClickProduct]);

    const volume = formatVolume(currentItem.properties);
    const hasDiscount = oldPrice && oldPrice !== currentPrice;
    const imageSrc = currentItem.images[0] || image;
    const discountPercent =
      currentItem.discountPrice &&
      currentItem.price &&
      currentItem.discountPrice < currentItem.price
        ? calcDiscountPercentage(currentItem.price, currentItem.discountPrice)
        : 0;
    const discountLabel =
      discountPercent > 0 ? `-${discountPercent}%` : undefined;
    const visibleBadges = company.isMarketplaceVariant
      ? badges.filter((badge) => badge.type !== 'discount')
      : badges;

    return (
      <div
        className={classes([
          styles.card,
          disabled && styles.disabled,
          className,
        ])}
        data-company={company.name}
      >
        <ProductImage
          src={imageSrc}
          alt={currentItem.name}
          size="xl"
          badges={
            <ProductBadges
              badges={visibleBadges.slice(0, 3)}
              size="m"
              variant="brand"
            />
          }
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          productSlug={product.slug}
          offerSku={currentItem.sku ?? ''}
          onProductClick={handleProductClick}
          cartItemsCount={cartItemsCount}
          totalCartPrice={totalCartPrice}
          isOutOfStock={isOutOfStock}
        />

        <div className={styles.infoWrapper}>
          <ProductInfo
            name={currentItem.name}
            volume={volume}
            rating={currentItem.ratingAverage}
            isRatingShown
            size="l"
            colorVariants={
              showColorVariants && colorVariants.length > 1
                ? colorVariants.map((v) => ({
                    color: v.value,
                    sku: v.sku,
                    name: v.value,
                  }))
                : undefined
            }
            onVariantSelect={selectOffer}
            productSlug={product.slug}
            currentSku={currentItem.sku ?? ''}
            multiBuyInfo={multiBuyInfo}
            cartItemsCount={cartItemsCount}
            promoRequiredQuantity={currentItem.promoRequiredQuantity}
          />
        </div>
        <PriceSection
          currentPrice={currentPrice}
          oldPrice={hasDiscount ? oldPrice : undefined}
          discount={hasDiscount ? discountLabel : undefined}
          size="l"
        />
        {disabled && <div className={styles.disabledOverlay} />}
      </div>
    );
  },
);

export default ProductCardXL;
